
<template>
  <div class="supplier-ordering-page">
    <LiquorOrderStatus
      v-if="roleChecked && !isOperations"
      :current-user-profile="resolvedUserProfile"
    />

    <LiquorOrderingNavBar
      v-model="activeSupplierName"
      :venue-id="activeVenueId"
    />

    <LiquorOrderingForm
      v-if="activeSupplierName && activeVenueId"
      :key="`${activeVenueId}:${activeSupplierName}`"
      :current-user-profile="resolvedUserProfile"
      :supplier-name="activeSupplierName"
      :supplier-email="activeSupplierEmail"
    />
  </div>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  ref,
  unref,
  watch,
} from "vue";

import {
  useVenueContext,
} from "@/scripts/useScripts/useVenueContext";

import {
  onAuthStateChanged,
} from "firebase/auth";

import {
  doc,
  getDoc,
} from "firebase/firestore";

import { auth, db } from "@/firebase";

import LiquorOrderingForm from "@/components/liquor/LiquorOrderingForm.vue";
import LiquorOrderStatus from "@/components/liquor/LiquorOrderStatus.vue";
import LiquorOrderingNavBar from "@/components/liquor/LiquorOrderingNavBar.vue";

const props = defineProps({
  currentUserProfile: {
    type: Object,
    default: () => ({}),
  },

  venueId: {
    type: String,
    default: "",
  },
});

const { venueId: contextVenueId } =
  useVenueContext();

const activeVenueId = computed(() =>
  String(unref(contextVenueId) || "").trim()
);

const activeSupplierName = ref("");

watch(
  activeVenueId,
  () => {
    activeSupplierName.value = "";
  },
  { flush: "sync" }
);

const roleChecked = ref(false);
const isOperations = ref(false);
const loadedUserProfile = ref({});

let authUnsubscribe = null;
let profileLoadSequence = 0;

const suppliedUserProfile = computed(() => {
  return props.currentUserProfile?.value ||
    props.currentUserProfile ||
    {};
});

const resolvedUserProfile = computed(() => {
  if (
    suppliedUserProfile.value &&
    Object.keys(suppliedUserProfile.value).length
  ) {
    return suppliedUserProfile.value;
  }

  return loadedUserProfile.value;
});

watch(
  suppliedUserProfile,
  (profile) => {
    applyProfileIfComplete(profile);
  },
  {
    immediate: true,
    deep: true,
  }
);

authUnsubscribe = onAuthStateChanged(
  auth,
  async (user) => {
    const currentSequence =
      ++profileLoadSequence;

    if (
      applyProfileIfComplete(
        suppliedUserProfile.value
      )
    ) {
      return;
    }

    roleChecked.value = false;
    loadedUserProfile.value = {};

    if (!user?.uid) {
      isOperations.value = false;
      roleChecked.value = true;
      return;
    }

    try {
      const profileSnapshot = await getDoc(
        doc(db, "users", user.uid)
      );

      if (
        currentSequence !== profileLoadSequence
      ) {
        return;
      }

      if (!profileSnapshot.exists()) {
        console.warn(
          "[SupplierOrderingPage] No user profile found:",
          user.uid
        );

        isOperations.value = false;
        roleChecked.value = true;
        return;
      }

      const profile = {
        id: profileSnapshot.id,
        ...profileSnapshot.data(),
      };

      loadedUserProfile.value = profile;
      isOperations.value =
        booleanValue(profile.isOperations);

      roleChecked.value = true;
    } catch (error) {
      if (
        currentSequence !== profileLoadSequence
      ) {
        return;
      }

      console.error(
        "[SupplierOrderingPage] User role could not be loaded:",
        error
      );

      isOperations.value = false;
      roleChecked.value = false;
    }
  }
);

onBeforeUnmount(() => {
  profileLoadSequence += 1;

  authUnsubscribe?.();
  authUnsubscribe = null;
});

function applyProfileIfComplete(profile) {
  if (
    !profile ||
    !Object.prototype.hasOwnProperty.call(
      profile,
      "isOperations"
    )
  ) {
    return false;
  }

  loadedUserProfile.value = profile;
  isOperations.value =
    booleanValue(profile.isOperations);

  roleChecked.value = true;
  return true;
}

function booleanValue(value) {
  return (
    value === true ||
    String(value ?? "")
      .trim()
      .toLowerCase() === "true"
  );
}

const supplierEmails = {
  "Think Drinks": "sales@thinkdrinks.co.uk",
  Stonegate: "",
  Dayla: "",
  "Swallo Drinks": "",
};

const activeSupplierEmail = computed(() => {
  return (
    supplierEmails[activeSupplierName.value] ||
    ""
  );
});
</script>

<style scoped>
.supplier-ordering-page {
  width: 100%;
}
</style>
